import React from 'react';
import "./LandingPage.css";
import { useState, useEffect } from 'react';
import { heroSectionApi, monthlyStoriesApi } from '../../APICalls/LandingPageAPI';

const formatDate = (date) => date.toISOString().split('T')[0];

const getMonthRange = (offset) => {
  const currentDate = new Date();
  const targetMonth = new Date(Date.UTC(
    currentDate.getUTCFullYear(),
    currentDate.getUTCMonth() - offset,
    1
  ));
  const startDate = new Date(targetMonth);
  const endDate = new Date(Date.UTC(
    targetMonth.getUTCFullYear(),
    targetMonth.getUTCMonth() + 1,
    0
  ));

  if (offset === 0) {
    endDate.setUTCDate(currentDate.getUTCDate());
  }

  return { startDate: formatDate(startDate), endDate: formatDate(endDate) };
};

const CACHE_TTL = 5 * 24 * 60 * 60 * 1000;

const getCachedStories = (cacheKey) => {
  const cachedValue = localStorage.getItem(cacheKey);

  if (!cachedValue) {
    return null;
  }

  try {
    const cachedEntry = JSON.parse(cachedValue);

    if (cachedEntry.expiresAt > Date.now() && Array.isArray(cachedEntry.data)) {
      return cachedEntry.data;
    }

    localStorage.removeItem(cacheKey);
  } catch (cacheError) {
    localStorage.removeItem(cacheKey);
  }

  return null;
};

const cacheStories = (cacheKey, stories) => {
  localStorage.setItem(cacheKey, JSON.stringify({
    data: stories,
    expiresAt: Date.now() + CACHE_TTL,
  }));
};

const LandingPage = () => {
  const [hero, setHero] = useState(null);
  const [stories, setStories] = useState([]);
  const [monthOffset, setMonthOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedStory, setSelectedStory] = useState(null);

  useEffect(() => {
    const loadHero = async () => {
      try {
        setHero(await heroSectionApi());
      } catch (requestError) {
        setError('The observatory could not reach NASA right now.');
      }
    };

    loadHero();
  }, []);

  useEffect(() => {
    const loadStories = async () => {
      setLoading(true);
      setError('');

      try {
        const { startDate, endDate } = getMonthRange(monthOffset);
        const cacheKey = `${startDate} - ${endDate}`;
        const cachedStories = getCachedStories(cacheKey);

        if (cachedStories) {
          setStories(cachedStories);
          return;
        }

        const monthlyStories = await monthlyStoriesApi(startDate, endDate);
        const imageStories = monthlyStories
          .filter((story) => story.media_type === 'image')
          .reverse();
        cacheStories(cacheKey, imageStories);
        setStories(imageStories);
      } catch (requestError) {
        setError('This month of stories is temporarily unavailable.');
        setStories([]);
      } finally {
        setLoading(false);
      }
    };

    loadStories();
  }, [monthOffset]);

  const monthLabel = () => {
    const { startDate, endDate } = getMonthRange(monthOffset);
    return `${startDate} - ${endDate}`;
  };

  const closeStory = () => setSelectedStory(null);
  
	return (
		<main className='landingPageContainer'>
      <section className='heroSection'>
        <div className='heroCopy'>
          <p className='eyebrow'>ET'S SPACE / DAILY TRANSMISSION</p>
          <h1>{hero?.title || 'A window into the universe.'}</h1>
          <p className='heroDescription'>
            {hero?.explanation || 'Loading today\'s image and the story behind it...'}
          </p>
          {hero?.date && <p className='heroDate'>Captured {hero.date}</p>}
        </div>
        <div className='heroImageWrap'>
          {hero?.url && <img src={hero.url} alt={hero.title} />}
          <span className='imageCaption'>NASA / APOD</span>
        </div>
      </section>

      <section className='feedSection'>
        <div className='sectionHeading'>
          <div>
            <p className='eyebrow'>THE ARCHIVE</p>
            <h2>A month in space</h2>
          </div>
          <p className='dateRange'>{monthLabel()}</p>
        </div>

        {error && <p className='statusMessage'>{error}</p>}
        {loading && <p className='statusMessage'>Receiving transmissions...</p>}
        {!loading && !error && (
          <div className='storyFeed'>
            {stories.map((story, index) => (
              <article
                className='storyCard'
                key={story.date}
                role='button'
                tabIndex='0'
                onClick={() => setSelectedStory(story)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    setSelectedStory(story);
                  }
                }}
              >
                <div className='storyNumber'>0{index + 1}</div>
                <img src={story.url} alt={story.title} />
                <div className='storyBody'>
                  <div className='storyMeta'>
                    <h3>{story.title}</h3>
                    <p className='storyDate'>{story.date}</p>
                  </div>
                  <button className='readMoreButton' type='button' onClick={() => setSelectedStory(story)}>
                    Read more
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}

        <nav className='pagination' aria-label='Story archive pages'>
          <button type='button' onClick={() => setMonthOffset((offset) => offset + 1)}>
            Previous month
          </button>
          <span>Month {monthOffset + 1}</span>
          <button type='button' disabled={monthOffset === 0} onClick={() => setMonthOffset((offset) => Math.max(0, offset - 1))}>
            Newer month
          </button>
        </nav>
      </section>

      {selectedStory && (
        <div className='storyOverlay' role='presentation' onClick={closeStory}>
          <section
            className='storyDetail'
            role='dialog'
            aria-modal='true'
            aria-labelledby='story-detail-title'
            onClick={(event) => event.stopPropagation()}
          >
            <button className='closeStory' type='button' aria-label='Close story' onClick={closeStory}>
              <span aria-hidden='true'>&#215;</span>
            </button>
            <div className='detailImageWrap'>
              <img src={selectedStory.hdurl || selectedStory.url} alt={selectedStory.title} />
            </div>
            <div className='detailBody'>
              <p className='storyDate'>{selectedStory.date}</p>
              <h2 id='story-detail-title'>{selectedStory.title}</h2>
              <p>{selectedStory.explanation}</p>
              <a
                className='hdLink'
                href={selectedStory.hdurl || selectedStory.url}
                target='_blank'
                rel='noreferrer'
              >
                View photo in HD <span aria-hidden='true'>&#8599;</span>
              </a>
            </div>
          </section>
        </div>
      )}
		</main>
	);
}    

export default LandingPage;